import { useCallback, useMemo } from "react";
import { useAuth as useOidcAuth } from "react-oidc-context";
import type { SigninRedirectArgs } from "oidc-client-ts";
import { clearReturningUser } from "@/shared/auth/returningUser";

/**
 * Neutral, provider-agnostic auth surface for the app.
 *
 * This is the ONLY module that depends on the OIDC client library; everything
 * else (routes, header, guards) consumes this hook. Swapping providers means
 * changing this file and {@link oidcConfig}, nothing else.
 */

export interface SignInOptions {
  /** IdP-specific home-realm hint (Entra `domain_hint`). */
  domainHint?: string | null;
  /** Pre-fills the username on the IdP sign-in page. */
  loginHint?: string | null;
  /** Forces a prompt, e.g. to let the user pick a different account. */
  prompt?: "login" | "select_account";
}

export interface AuthState {
  /** A non-expired user is present. */
  isAuthenticated: boolean;
  /** The client has finished its initial load / redirect-callback handling. */
  isAuthResolved: boolean;
  /** Last auth error (sign-in, sign-out, or silent-renew failure), if any. */
  error: Error | null;
  /** Redirects to the IdP to sign in. */
  signIn(options?: SignInOptions): Promise<void>;
  /** Redirects to the IdP end-session endpoint to sign out. */
  signOut(): Promise<void>;
}

export function toSigninArgs(options?: SignInOptions): SigninRedirectArgs | undefined {
  if (!options) {
    return undefined;
  }

  const loginHint = options.loginHint?.trim();
  const domainHint = options.domainHint?.trim();

  return {
    ...(loginHint ? { login_hint: loginHint } : {}),
    ...(options.prompt ? { prompt: options.prompt } : {}),
    ...(domainHint ? { extraQueryParams: { domain_hint: domainHint } } : {}),
  };
}

export function useAuth(): AuthState {
  const auth = useOidcAuth();
  // oidc-client-ts returns stable, already-bound methods; `this` is never rebound.
  // oxlint-disable-next-line typescript/unbound-method
  const { isAuthenticated, isLoading, error, signinRedirect, signoutRedirect } = auth;

  // Catch/log inside the hook so callers can fire-and-forget (`void signIn()`)
  // without risking unhandled promise rejections if the redirect fails (bad
  // config, callback processing error). The failure is also surfaced via
  // `error` for the UI.
  const signIn = useCallback(
    async (options?: SignInOptions) => {
      try {
        await signinRedirect(toSigninArgs(options));
      } catch (err) {
        console.error("[auth] sign-in failed", err);
      }
    },
    [signinRedirect],
  );
  const signOut = useCallback(async () => {
    try {
      // Clear the returning-user flag so a deliberate sign-out lands the user
      // back on the landing page rather than auto-redirecting them to the IdP.
      clearReturningUser();
      await signoutRedirect();
    } catch (err) {
      console.error("[auth] sign-out failed", err);
    }
  }, [signoutRedirect]);

  return useMemo<AuthState>(
    () => ({ isAuthenticated, isAuthResolved: !isLoading, error: error ?? null, signIn, signOut }),
    [isAuthenticated, isLoading, error, signIn, signOut],
  );
}
